import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { RouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import generateDefaultProposalName from 'global/utils/generateDefaultProposalName';
import BulletTabsDelegate, { BulletTabStep } from 'store/general/bulletTabsDelegate';
import FavoriteProposalStepController from 'store/modules/initiate-quote/favoriteProposalStepController';
import ProposalExperienceStepController from 'store/modules/initiate-quote/proposalExperienceStepController';
import {
  ProposalExperience,
  ProposalExperiencePayload,
  ProposalExperiencePolicy,
} from 'store/modules/initiate-quote/types';
import BasicInfoStepController from './basicInfoStepController';

interface DomainParams {
  groupId: string;
}

export default class NEDDInitiateProposalPageStore {
  public domainParams: DomainParams;
  public initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public bulletTabsDelegate: BulletTabsDelegate;

  // Step controllers
  public basicInfoStepController: BasicInfoStepController;
  public proposalExperienceStepController: ProposalExperienceStepController;
  public favoriteProposalStepController: FavoriteProposalStepController;

  public get proposalExperience(): ProposalExperience | null {
    return this.proposalExperienceStepController.proposalExperience;
  }

  public get stepControllers(): BulletTabStep[] {
    const steps: BulletTabStep[] = [this.basicInfoStepController, this.proposalExperienceStepController];
    if (this.proposalExperience === 'FAVORITE') {
      steps.push(this.favoriteProposalStepController);
    }
    return steps;
  }

  // eslint-disable-next-line class-methods-use-this
  private _getExperiencePolicy = (): ProposalExperiencePolicy => {
    return {
      newBusinessExperiences: ['SHELF', 'FAVORITE'],
      renewalExperiences: ['RENEWAL_SHELF'],
    };
  };

  private _getPayload = (): ProposalExperiencePayload => {
    const { effectiveDate, zip, state, county, proposalName } =
      this.basicInfoStepController.form.getFormDataObject() as Record<string, string>;

    return {
      proposalName,
      browsingCriteria: {
        effectiveDate,
        zipCode: zip,
        state,
        county,
        shortName: {},
      },
    };
  };

  public async getInitialData(): Promise<void> {
    this.initiateQuoteFlowDelegate.setIsInitialDataLoading(true);

    await this.initiateQuoteFlowDelegate.getFlowInitialData();
    runInAction(() => {
      const address = this.initiateQuoteFlowDelegate.groupModule.group.contactInfo.primaryAddress;

      this.basicInfoStepController.form.populateFields({
        proposalName: generateDefaultProposalName(this.initiateQuoteFlowDelegate.groupModule.groupName),
        zip: address.zip || '',
        state: address.state || '',
        county: address.county || '',
      });
      this.initiateQuoteFlowDelegate.setIsInitialDataLoading(false);
    });
  }

  public exitFromFirstStep(): void {
    redirectToRoute(RouteV2Id.GROUP_HUB, { groupId: this.domainParams.groupId });
  }

  constructor(params: DomainParams) {
    this.domainParams = params;

    this.initiateQuoteFlowDelegate = new InitiateQuoteFlowDelegate({
      domainParams: { groupId: params.groupId, nextPageId: RouteV2Id.QUOTE_CHECKLIST },
    });
    this.basicInfoStepController = new BasicInfoStepController(this.initiateQuoteFlowDelegate);
    this.proposalExperienceStepController = new ProposalExperienceStepController({
      initiateQuoteFlowDelegate: this.initiateQuoteFlowDelegate,
      getPayload: this._getPayload,
      getPolicy: this._getExperiencePolicy,
    });
    this.favoriteProposalStepController = new FavoriteProposalStepController(
      this.initiateQuoteFlowDelegate,
      this._getPayload,
    );

    this.bulletTabsDelegate = new BulletTabsDelegate({
      getStepControllers: (): BulletTabStep[] => this.stepControllers,
      exitFromFirstStep: (): void => this.exitFromFirstStep(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const NEDDInitiateProposalPageStoreContext = createContext(
  new NEDDInitiateProposalPageStore({
    groupId: '',
  }),
);

export const useNEDDInitiateProposalPageStore = (): NEDDInitiateProposalPageStore => {
  return useContext(NEDDInitiateProposalPageStoreContext);
};

export { NEDDInitiateProposalPageStoreContext };
